import { api } from "@/api/client";
import { supabase } from "@/lib/supabase";
import type { Capabilities, OrgContext, SubscriptionContext } from "@/types";
import type {
  AcceptInviteBody,
  AcceptInviteContext,
  AcceptPlatformInviteBody,
  ActiveOrganizationResponse,
  CompleteWorkerOnboardingBody,
  MeResponse,
} from "@/types/api/me";

export async function postEndStaffImpersonation(): Promise<{ ok: true }> {
  const { data } = await api.post<{ ok: true }>("/v1/me/end-staff-impersonation");
  return data;
}

export async function postActivateStaffImpersonation(body: {
  activationNonce: string;
}): Promise<{ ok: true; staffImpersonation: MeResponse["staffImpersonation"] }> {
  const { data } = await api.post<{
    ok: true;
    staffImpersonation: MeResponse["staffImpersonation"];
  }>("/v1/me/activate-staff-impersonation", body);
  return data;
}

export async function postCancelPendingStaffImpersonation(): Promise<{ ok: true }> {
  const { data } = await api.post<{ ok: true }>("/v1/me/cancel-pending-staff-impersonation");
  return data;
}

export async function getMe(
  accessTokenHint?: string | null,
  options?: { signal?: AbortSignal },
): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>("/v1/me", {
    accessTokenHint,
    signal: options?.signal,
  });
  return data;
}

export async function putActiveOrganization(
  organizationId: string,
): Promise<ActiveOrganizationResponse> {
  const { data } = await api.put<ActiveOrganizationResponse>("/v1/me/active-organization", {
    organizationId,
  });
  return data;
}

export async function postOnboard(body: {
  fullName: string;
  birthDate: string;
  orgName: string;
  legalName: string;
  cnpj: string;
  countryKey: "BR" | "US";
}): Promise<{
  organization: OrgContext;
  subscription: SubscriptionContext | null;
  capabilities: Capabilities | null;
}> {
  const { data } = await api.post<{
    organization: OrgContext;
    subscription: SubscriptionContext | null;
    capabilities: Capabilities | null;
  }>("/v1/auth/onboard", body);
  return data;
}

export async function patchProfile(body: {
  fullName: string;
  birthDate?: string;
}): Promise<{ displayName: string; birthDate: string | null }> {
  const { data } = await api.patch<{ displayName: string; birthDate: string | null }>(
    "/v1/me/profile",
    body,
  );
  return data;
}

export async function patchPreferredLanguage(
  language: "en" | "pt-BR",
): Promise<"en" | "pt-BR"> {
  const { data } = await api.patch<{ preferredLanguage: "en" | "pt-BR" }>("/v1/me/language", {
    language,
  });
  return data.preferredLanguage;
}

export async function postAvatar(file: File): Promise<{ avatarUrl: string | null }> {
  const mimeType = file.type || "image/jpeg";
  const { data: prepared } = await api.post<{
    imageId: string;
    storagePath: string;
    upload: { bucket: string; path: string; token: string; signedUrl: string };
  }>("/v1/me/avatar/prepare", { mimeType });

  if (!supabase) {
    throw new Error(
      "Supabase is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
    );
  }

  const { error } = await supabase.storage
    .from(prepared.upload.bucket)
    .uploadToSignedUrl(prepared.upload.path, prepared.upload.token, file, {
      contentType: mimeType,
      upsert: true,
    });
  if (error) {
    throw new Error(`Avatar upload failed: ${error.message}`);
  }

  const { data } = await api.post<{ avatarUrl: string | null }>("/v1/me/avatar/confirm", {
    imageId: prepared.imageId,
    storagePath: prepared.storagePath,
    mimeType,
    sizeBytes: file.size,
  });
  return data;
}

export async function deleteAvatar(): Promise<{ avatarUrl: null }> {
  const { data } = await api.delete<{ avatarUrl: null }>("/v1/me/avatar");
  return data;
}

export async function postAcceptInvite(body: AcceptInviteBody): Promise<{
  displayName: string;
  memberStatus: "onboarded";
  organizationId: string;
}> {
  const { data } = await api.post<{
    displayName: string;
    memberStatus: "onboarded";
    organizationId: string;
  }>("/v1/me/accept-invite", body);
  return data;
}

export async function postCompleteWorkerOnboarding(body: CompleteWorkerOnboardingBody): Promise<{
  displayName: string;
  organizationId: string;
}> {
  const { data } = await api.post<{
    displayName: string;
    organizationId: string;
  }>("/v1/me/complete-worker-onboarding", body);
  return data;
}

export async function getAcceptInviteContext(organizationId: string): Promise<AcceptInviteContext> {
  const { data } = await api.get<AcceptInviteContext>("/v1/me/accept-invite-context", {
    params: { organizationId },
  });
  return data;
}

export async function postAcceptPlatformInvite(body: AcceptPlatformInviteBody): Promise<{
  displayName: string;
  platformInviteStatus: "accepted";
}> {
  const { data } = await api.post<{
    displayName: string;
    platformInviteStatus: "accepted";
  }>("/v1/me/accept-platform-invite", body);
  return data;
}
